<template>
	<div class="touch-card-data__metrics" :class="`touch-card-data__metrics--${metricLayout}`">
		<div v-for="column in visibleColumns" :key="column.field" class="touch-card-data__metric" :class="`touch-card-data__metric--${effectiveType(column) || 'gap'}`">
			<!-- The progress metric's label always repeats the page/panel's own KPI title, so it's dropped here. -->
			<span v-if="effectiveType(column) !== 'progress'" class="text-caption text-uppercase text-medium-emphasis">{{ column.label }}</span>

			<template v-if="effectiveType(column) === 'progress'">
				<div class="touch-card-data__progress" :class="valueClass(column)">
					<ProgressBarRenderer :params="progressParams(column)" />
				</div>
			</template>

			<span v-else-if="effectiveType(column) === 'chip'" class="touch-card-data__tags">
				<v-chip v-for="(tag, index) in tags(column)" :key="index" size="x-small" variant="outlined" label :class="tag.class">
					<v-icon v-if="tag.icon">{{ tag.icon }}</v-icon>
					<template v-if="tag.text">{{ tag.text }}</template>
				</v-chip>
			</span>

			<span v-else-if="effectiveType(column) === 'icon'" class="touch-card-data__icons">
				<v-icon v-for="(tag, index) in tags(column)" :key="index" :class="tag.class">{{ tag.icon }}</v-icon>
				<span v-if="!tags(column).length" class="text-disabled">—</span>
			</span>

			<span v-else-if="effectiveType(column) === 'switch'" class="touch-card-data__control" @click.stop @mousedown.stop>
				<v-switch :model-value="Boolean(getValue(column.field))" :color="column.color || 'primary'" :disabled="isDisabled(column)" density="compact" hide-details inset @update:model-value="onCellChange(column, $event)" />
			</span>

			<span v-else-if="effectiveType(column) === 'input'" class="touch-card-data__control" @click.stop @mousedown.stop>
				<v-text-field :model-value="getValue(column.field)" type="number" inputmode="numeric" :min="column.min ?? 0" :max="column.max ?? undefined" :step="column.step ?? 1" :disabled="isDisabled(column)" density="compact" variant="outlined" hide-details @change="onCellChange(column, Number($event.target.value) || 0)" />
			</span>

			<span v-else-if="effectiveType(column) === 'multiselect'" class="touch-card-data__control" @click.stop @mousedown.stop>
				<SelectControl :items="column.items || []" title-key="name" item-value="id" :multiple="true" :model-value="selectedIds(column)" :readonly="isDisabled(column)" :clearable="false" density="compact" :max-displayed-chips="1" @update:model-value="onMultiSelectChange(column, $event)" />
			</span>

			<span v-else class="touch-card-data__evo-container">
				<div class="touch-card-data__gap">
					<template v-if="column.fromField">
						{{ formatValue(column, column.fromField) }}
						<span class="text-disabled mx-1">→</span>
					</template>
					<span class="font-weight-bold">{{ formatValue(column) }}</span>
				</div>
				<span v-if="column.evoField && hasEvo(column)" class="touch-card-data__evo" :class="evoClass(column)">
					{{ formatEvo(column) }}
				</span>
			</span>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';
import SelectControl from '@/components/controls/SelectControl.vue';

const props = defineProps({
	row: { type: Object, default: () => ({}) },
	columns: { type: Array, default: () => [] },
	metricLayout: { type: String, default: 'grid' },
	// Columns flagged `detail` (e.g. reference/total-references counts) only render once the caller says
	// this row/group is in its expanded state - collapsed groups and leaf cards hide them by default.
	expanded: { type: Boolean, default: true },
	// True when this instance renders a GROUP AGGREGATION row (TouchCardGroup's header). Aggregates are plain
	// numbers, so decorated and interactive types must degrade to text there and must never emit.
	aggregate: { type: Boolean, default: false },
});

const emit = defineEmits(['cell-change']);

const visibleColumns = computed(() => props.columns.filter((column) => !column.detail || props.expanded));

// Single choke point for the type switch. 'progress' is deliberately preserved in aggregate mode: the grouped
// card headers of KpiBuilder/SpsBuilder rely on it.
function effectiveType(column) {
	return props.aggregate && column.type !== 'progress' ? null : column.type;
}

function tags(column) {
	const value = column.tagsField ? props.row?.[column.tagsField] : null;
	return Array.isArray(value) ? value : [];
}

function isDisabled(column) {
	return column.disabledField ? Boolean(props.row?.[column.disabledField]) : false;
}

function onCellChange(column, value) {
	if (props.aggregate) return;
	emit('cell-change', { row: props.row, field: column.field, value });
}

function selectedIds(column) {
	const value = props.row?.[column.field];
	return Array.isArray(value) ? value : [];
}

function onMultiSelectChange(column, next) {
	const value = Array.isArray(next) ? next.map(Number) : [];
	const current = selectedIds(column);
	if (value.length === current.length && value.every((id, index) => id === current[index])) return;
	onCellChange(column, value);
}

function getValue(field) {
	return props.row && field ? (props.row[field] ?? '') : '';
}

function formatNumber(value, decimals) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '0';
	const rounded = decimals > 0 ? Math.round(numeric * Math.pow(10, decimals)) / Math.pow(10, decimals) : Math.round(numeric);
	return rounded.toLocaleString();
}

// A column-level formatMetric owns the whole rendering (sign, unit, percentage) - the KPI and SPS
// builders reuse the very same formatters they already give to their ag-Grid value formatters.
function formatValue(column, field = column.field) {
	const rawValue = getValue(field);
	if (typeof column.formatMetric === 'function') {
		return column.formatMetric(rawValue);
	}
	return `${formatNumber(rawValue, column.decimals)}${column.suffix}`;
}

function progressParams(column) {
	return {
		value: getValue(column.field),
		data: {
			objective: column.objectiveField ? getValue(column.objectiveField) : null,
			color: column.colorField ? getValue(column.colorField) : null,
		},
		isPercentage: column.isPercentage,
		hideObjective: column.hideObjective,
		needLabel: column.needObjectiveLabel,
		objectiveLabel: column.objectiveLabel,
		barColor: column.barColor,
		formatMetric: column.formatMetric,
		differenceValue: column.deltaField ? getValue(column.deltaField) : null,
		differenceFormatted: column.deltaField ? formatDeltaValue(column) : null,
	};
}

function formatDeltaValue(column) {
	const rawValue = getValue(column.deltaField);
	if (typeof column.deltaFormatMetric === 'function') {
		return column.deltaFormatMetric(rawValue);
	}

	const numeric = Number(rawValue);
	if (!Number.isFinite(numeric)) return '';
	const rounded = Math.round(numeric * 10) / 10;
	return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function valueClass(column) {
	const classColor = column.classColorField ? getValue(column.classColorField) : '';
	return classColor ? `touch-card-data__progress--${classColor}` : '';
}

function hasEvo(column) {
	const value = props.row?.[column.evoField];
	return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function evoClass(column) {
	return Number(props.row[column.evoField]) >= 0 ? 'positive' : 'negative';
}

function formatEvo(column) {
	const rawValue = props.row[column.evoField];
	if (typeof column.evoFormatMetric === 'function') {
		return column.evoFormatMetric(rawValue);
	}

	const value = Number(rawValue);
	const sign = value > 0 ? '+' : '';
	return `${sign}${Math.round(value * 10) / 10}%`;
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.touch-card-data__metrics {
	display: grid;

	&--grid {
		grid-template-columns: repeat(2, 1fr);
	}

	&--rows {
		grid-template-columns: 1fr;
	}

	.touch-card-data__metric {
		display: flex;
		flex-direction: column;
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		padding-top: 2px;
		line-height: 28px;
		min-width: 0;

		&:first-child {
			border-top: thin dashed rgba(var(--v-border-color), var(--v-border-opacity));
		}

		&--progress {
			grid-column: 1 / -1;
		}

		> span {
			font-size: $font-size;
		}

		&--chip,
		&--icon {
			.touch-card-data__tags,
			.touch-card-data__icons {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				flex-wrap: wrap;
				gap: 4px;
				min-width: 0;
			}
		}

		&--switch,
		&--input,
		&--multiselect {
			.touch-card-data__control {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				min-width: 0;

				.v-input {
					flex: 0 1 auto;
					max-width: 120px;
				}
			}
		}

		// A reasons picker sizes to its own selection chips instead of stretching to fill the row.
		&--multiselect .touch-card-data__control {
			flex: 0 1 auto;
			max-width: 100%;

			.v-input {
				flex: 0 1 auto;
				width: fit-content;
				max-width: 100%;
			}
		}
	}

	&--rows .touch-card-data__metric:not(.touch-card-data__metric--progress) {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.touch-card-data__evo-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 4px;
		flex: 1;
		min-width: 0;
	}

	.touch-card-data__evo {
		display: inline-flex;
		align-items: center;
		justify-content: flex-end;
		font-size: $font-size-small;
		font-weight: 600;
		width: 60px;

		&.positive {
			color: $color-success;
		}

		&.negative {
			color: $color-error;
		}
	}

	.touch-card-data__progress {
		line-height: normal;

		:deep(.progress-bar-renderer) {
			height: auto;
			padding: 2px 0 4px;
		}

		&--positive :deep(.progress-bar-renderer__value) {
			color: $color-success;
		}

		&--warning :deep(.progress-bar-renderer__value) {
			color: $color-warning;
		}

		&--negative :deep(.progress-bar-renderer__value) {
			color: $color-error;
		}
	}
}
</style>
